
import { useState } from 'react';
import { UseFormRegister, UseFormSetValue, UseFormGetValues } from 'react-hook-form';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuCheckboxItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { Check, ChevronDown } from 'lucide-react';
import type { FormData } from '../types';

// US States array
const US_STATES = [
  'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut', 
  'Delaware', 'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa', 
  'Kansas', 'Kentucky', 'Louisiana', 'Maine', 'Maryland', 'Massachusetts', 'Michigan', 
  'Minnesota', 'Mississippi', 'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire', 
  'New Jersey', 'New Mexico', 'New York', 'North Carolina', 'North Dakota', 'Ohio', 
  'Oklahoma', 'Oregon', 'Pennsylvania', 'Rhode Island', 'South Carolina', 'South Dakota', 
  'Tennessee', 'Texas', 'Utah', 'Vermont', 'Virginia', 'Washington', 'West Virginia', 
  'Wisconsin', 'Wyoming'
];

interface StatesServedFieldProps {
  register: UseFormRegister<FormData>;
  setValue: UseFormSetValue<FormData>;
  getValues: UseFormGetValues<FormData>;
}

export const StatesServedField = ({ register, setValue, getValues }: StatesServedFieldProps) => {
  // Register the field to ensure form validation still works
  const registeredField = register('states_served');
  
  // Get initial states from form values
  const initialStates = getValues('states_served');
  
  // Track selected states internally
  const [selectedStates, setSelectedStates] = useState<string[]>(
    Array.isArray(initialStates) ? initialStates : 
    (typeof initialStates === 'string' && initialStates.trim() !== '') ? 
      initialStates.split(',').map(s => s.trim()) : []
  );

  // Toggle individual state selection
  const toggleState = (state: string) => {
    const newSelectedStates = selectedStates.includes(state)
      ? selectedStates.filter(s => s !== state)
      : [...selectedStates, state];
    
    setSelectedStates(newSelectedStates);
    setValue('states_served', newSelectedStates, { shouldValidate: true });
  };

  // Select all states at once
  const selectAllStates = () => {
    setSelectedStates([...US_STATES]);
    setValue('states_served', US_STATES, { shouldValidate: true });
  };

  // Clear all selected states
  const clearAllStates = () => {
    setSelectedStates([]);
    setValue('states_served', [], { shouldValidate: true });
  };

  return (
    <div className="space-y-2">
      <Label htmlFor="states_served">States Served</Label>
      
      <div className="flex gap-2 flex-wrap">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" className="flex justify-between w-full">
              <span>{selectedStates.length > 0 
                ? `${selectedStates.length} state${selectedStates.length !== 1 ? 's' : ''} selected` 
                : 'Select states'}
              </span>
              <ChevronDown className="h-4 w-4 opacity-50" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="w-56 bg-background">
            <div className="p-2 flex gap-2">
              <Button size="sm" onClick={selectAllStates} className="flex-1">Select All</Button>
              <Button size="sm" onClick={clearAllStates} variant="outline" className="flex-1">Clear All</Button>
            </div>
            <DropdownMenuSeparator />
            <ScrollArea className="h-72">
              <DropdownMenuGroup className="p-1">
                {US_STATES.map((state) => (
                  <DropdownMenuCheckboxItem
                    key={state}
                    checked={selectedStates.includes(state)}
                    onCheckedChange={() => toggleState(state)}
                  >
                    {state}
                  </DropdownMenuCheckboxItem>
                ))}
              </DropdownMenuGroup>
            </ScrollArea>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      
      {selectedStates.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1">
          {selectedStates.map((state) => (
            <div 
              key={state} 
              className="text-xs bg-primary/10 text-primary rounded-md px-2 py-1 flex items-center"
            >
              {state}
              <button 
                type="button"
                className="ml-1 text-primary hover:text-primary-dark"
                onClick={() => toggleState(state)}
              >
                ×
              </button>
            </div>
          ))}
        </div>
      )}
      
      {/* Hidden input for form data */}
      <input
        type="hidden"
        id="states_served"
        name={registeredField.name}
        ref={registeredField.ref}
        onChange={registeredField.onChange}
        onBlur={registeredField.onBlur}
        value={selectedStates.join(',')}
      />
    </div>
  );
};
